import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CaretLeft,
  Plus,
  Receipt,
  Envelope,
  TrashSimple,
  Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  LINE_CATEGORIES,
  LINE_CATEGORY_LABELS,
  addInvoiceLineItem,
  adminMarkInvoicePaid,
  centsToUsd,
  createDraftInvoice,
  disableAutopay,
  getBillingProfileForFamily,
  getOrganizationBillingDefaults,
  invoiceStatusLabel,
  issueInvoice,
  listInvoicesForProfile,
  removeInvoiceLineItem,
  updateBillingProfile,
  voidInvoice,
} from "@/lib/billing";
import { notifyInvoiceIssued } from "@/lib/notifications";
import { fetchInvoicePdfBlob, emailInvoice } from "@/lib/admin/billingActions";
import AutopayStatusChip from "@/components/billing/AutopayStatusChip";
import { getTuitionPlanForProfile } from "@/lib/tuitionPlans";
import TuitionPlanCard from "@/components/billing/TuitionPlanCard";

export default function AdminBillingFamily() {
  const { familyId } = useParams();
  const { profile } = useAuth();
  const [prof, setProf] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [orgDefaults, setOrgDefaults] = useState(null);
  const [plan, setPlan] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const bp = await getBillingProfileForFamily(familyId);
      setProf(bp);
      if (bp?.id) {
        const [list, org, tp] = await Promise.all([
          listInvoicesForProfile(bp.id),
          getOrganizationBillingDefaults(profile?.organization_id),
          getTuitionPlanForProfile(bp.id),
        ]);
        setInvoices(list);
        setOrgDefaults(org);
        setPlan(tp);
      }
    } catch (err) {
      toast.error(err.message || "Could not load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId]);

  const onCreateInvoice = async (draft) => {
    try {
      const inv = await createDraftInvoice({
        organizationId: profile.organization_id,
        billingProfileId: prof.id,
        title: draft.title,
        dueDate: draft.dueDate || null,
        createdBy: profile.id,
      });
      for (let i = 0; i < draft.lines.length; i += 1) {
        const l = draft.lines[i];
        // eslint-disable-next-line no-await-in-loop
        await addInvoiceLineItem({
          invoiceId: inv.id,
          category: l.category,
          description: l.description,
          quantity: l.quantity,
          unitAmountCents: l.unitAmountCents,
          sortOrder: i,
        });
      }
      toast.success("Invoice created as draft");
      setShowNew(false);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not create invoice");
    }
  };

  const onIssue = async (invoiceId) => {
    try {
      const issued = await issueInvoice(invoiceId);
      toast.success("Invoice issued to family");
      await load();
      // Notify the family (async; failures don't block the happy path).
      try {
        notifyInvoiceIssued({
          invoice_id: issued?.id || invoiceId,
          billing_email: prof?.billing_email,
          billing_profile_family_name: prof?.family?.display_name,
          amount_total_cents: issued?.amount_total_cents || 0,
          currency: (issued?.currency || "usd").toLowerCase(),
          due_date: issued?.due_date || null,
          billing_profile_id: prof?.id,
          parent_profile_id: prof?.family?.primary_contact_profile_id || null,
          organization_id: prof?.organization_id,
          organization_name: prof?.family?.organization?.display_name || prof?.family?.organization?.name || null,
        });
      } catch {
        // Swallow — admin already saw success; log panel owns the truth.
      }
    } catch (err) {
      toast.error(err.message || "Could not issue invoice");
    }
  };

  const onVoid = async (invoiceId) => {
    if (!window.confirm("Void this invoice? This cannot be undone.")) return;
    try {
      await voidInvoice(invoiceId);
      toast.success("Invoice voided");
      await load();
    } catch (err) {
      toast.error(err.message || "Could not void");
    }
  };

  const onMarkPaid = async (invoiceId) => {
    const note = window.prompt(
      "Record a manual payment (cash, check, wire). Optional note:",
      "",
    );
    if (note === null) return;
    try {
      await adminMarkInvoicePaid({ invoiceId, note });
      toast.success("Invoice marked as paid");
      await load();
    } catch (err) {
      toast.error(err.message || "Could not mark paid");
    }
  };

  const onUpdateEmail = async (newEmail) => {
    try {
      const updated = await updateBillingProfile(prof.id, {
        billing_email: newEmail,
      });
      setProf((prev) => ({ ...prev, ...updated }));
      toast.success("Billing email updated");
    } catch (err) {
      toast.error(err.message || "Could not update");
    }
  };

  const onUpdateTuition = async (cents) => {
    try {
      const updated = await updateBillingProfile(prof.id, {
        monthly_tuition_override_cents: cents,
      });
      setProf((prev) => ({ ...prev, ...updated }));
      toast.success(cents == null ? "Tuition override cleared" : "Tuition override saved");
    } catch (err) {
      toast.error(err.message || "Could not update");
    }
  };

  const onUpdateSiblingDiscount = async (percent) => {
    try {
      const updated = await updateBillingProfile(prof.id, {
        sibling_discount_percent_override: percent,
      });
      setProf((prev) => ({ ...prev, ...updated }));
      toast.success(
        percent == null ? "Sibling discount reset to org default" : "Sibling discount override saved",
      );
    } catch (err) {
      toast.error(err.message || "Could not update");
    }
  };

  const onDisableAutopay = async () => {
    if (!prof?.id) return;
    if (!window.confirm("Disable autopay for this family? They'll need to pay future invoices manually.")) return;
    try {
      await disableAutopay(prof.id);
      toast.success("Autopay disabled");
      await load();
    } catch (err) {
      toast.error(err.message || "Could not disable autopay");
    }
  };

  return (
    <div data-testid="admin-billing-family" className="space-y-6">
      <Link
        to="/admin/billing"
        data-testid="billing-family-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        Back to billing
      </Link>

      {loading || !prof ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : (
        <>
          <header className="rounded-2xl border border-neutral-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
              Billing profile
            </p>
            <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
              <h1 className="font-heading text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">
                {prof.family?.display_name || "Family"}
              </h1>
              <div
                data-testid="admin-family-autopay"
                className="flex flex-wrap items-center gap-2"
              >
                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  <Lightning size={12} weight="duotone" className="text-primary-hover" />
                  Autopay
                </span>
                <AutopayStatusChip
                  status={prof.autopay_status || (prof.autopay ? "active" : "none")}
                  testId="admin-family-autopay-chip"
                />
                {(prof.autopay_status === "active" ||
                  prof.autopay_status === "pending_setup") && (
                  <button
                    type="button"
                    data-testid="admin-family-autopay-disable"
                    onClick={onDisableAutopay}
                    className="inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-1 text-[10px] font-semibold text-[#C6851E] hover:bg-[#C6851E]/5"
                  >
                    Disable
                  </button>
                )}
              </div>
            </div>
            {prof.autopay_failure_reason ? (
              <p
                data-testid="admin-family-autopay-reason"
                className="mt-1 text-[11px] text-[#C6851E]"
              >
                {prof.autopay_failure_reason}
              </p>
            ) : null}
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <label className="block">
                <span className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  <Envelope size={12} weight="duotone" />
                  Billing email
                </span>
                <input
                  data-testid="billing-family-email"
                  defaultValue={prof.billing_email || ""}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== prof.billing_email) onUpdateEmail(v);
                  }}
                  placeholder="billing@family.com"
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </label>
              <label className="block">
                <span className="mb-1 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  Monthly tuition override (optional)
                </span>
                <input
                  data-testid="billing-family-tuition-override"
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={
                    prof.monthly_tuition_override_cents != null
                      ? (prof.monthly_tuition_override_cents / 100).toFixed(2)
                      : ""
                  }
                  placeholder="Fallback: classroom tuition"
                  onBlur={(e) => {
                    const s = e.target.value.trim();
                    const next = s === ""
                      ? null
                      : Math.round(parseFloat(s) * 100);
                    if (next !== prof.monthly_tuition_override_cents) {
                      onUpdateTuition(next);
                    }
                  }}
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="mt-1 block text-[10px] text-neutral-500">
                  Leave blank to use each child's classroom rate.
                </span>
              </label>
              <label className="block">
                <span className="mb-1 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                  Sibling discount override (%)
                </span>
                <input
                  data-testid="billing-family-sibling-discount"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  defaultValue={
                    prof.sibling_discount_percent_override != null
                      ? String(prof.sibling_discount_percent_override)
                      : ""
                  }
                  placeholder={`Org default: ${orgDefaults?.sibling_discount_percent ?? 0}%`}
                  onBlur={(e) => {
                    const s = e.target.value.trim();
                    const next =
                      s === ""
                        ? null
                        : Math.max(0, Math.min(100, parseInt(s, 10) || 0));
                    if (next !== prof.sibling_discount_percent_override) {
                      onUpdateSiblingDiscount(next);
                    }
                  }}
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="mt-1 block text-[10px] text-neutral-500">
                  Applied to the cheapest sibling when ≥ 2 children are enrolled.
                </span>
              </label>
            </div>
            <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
              {prof.stripe_customer_id
                ? `Stripe customer: ${prof.stripe_customer_id.slice(0, 12)}…`
                : "No Stripe customer yet"}
            </p>
          </header>

          <TuitionPlanCard
            billingProfileId={prof.id}
            plan={plan}
            onChange={() => load()}
          />

          <div className="flex justify-end">
            <button
              type="button"
              data-testid="billing-new-invoice"
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98]"
            >
              <Plus size={14} weight="bold" />
              New invoice
            </button>
          </div>

          {showNew && (
            <NewInvoiceForm
              onCancel={() => setShowNew(false)}
              onSubmit={onCreateInvoice}
            />
          )}

          {invoices.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500">
              No invoices yet.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="billing-family-invoices">
              {invoices.map((inv) => (
                <li key={inv.id}>
                  <InvoiceAdminCard
                    invoice={inv}
                    onIssue={() => onIssue(inv.id)}
                    onVoid={() => onVoid(inv.id)}
                    onMarkPaid={() => onMarkPaid(inv.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function InvoiceAdminCard({ invoice, onIssue, onVoid, onMarkPaid }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);

  const onDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      const url = await fetchInvoicePdfBlob(invoice.id);
      // Open in a new tab so admins can preview or print without losing context.
      const win = window.open(url, "_blank");
      if (!win) {
        // Popup-blocked: fall back to a download click.
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoice-${invoice.id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      // Revoke after a moment so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      toast.error(err.message || "Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const onEmailInvoice = async () => {
    if (!window.confirm("Send this invoice as a PDF email to the parent?")) return;
    setEmailBusy(true);
    try {
      const res = await emailInvoice({ invoiceId: invoice.id });
      toast.success(`Emailed to ${res.to}`);
    } catch (err) {
      // The fetch helper already extracts `detail` into err.message — surface
      // it directly so admins see the friendly "set RESEND_API_KEY" copy.
      toast.error(err.message || "Could not email invoice");
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div
      data-testid={`admin-invoice-${invoice.id}`}
      data-status={invoice.status}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <Receipt size={18} weight="duotone" className="text-primary-hover" />
            <h3 className="font-heading text-base font-semibold text-neutral-900">
              {invoice.title}
            </h3>
            <StatusTag status={invoice.status} />
          </div>
          <p className="mt-1 text-xs text-neutral-500">
            Issue {new Date(invoice.issue_date).toLocaleDateString()}
            {invoice.due_date ? ` · Due ${new Date(invoice.due_date).toLocaleDateString()}` : ""}
            {invoice.invoice_emailed_at
              ? ` · Emailed ${new Date(invoice.invoice_emailed_at).toLocaleDateString()}`
              : ""}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
            Total
          </p>
          <p className="font-heading text-xl font-semibold text-neutral-900">
            {centsToUsd(invoice.amount_total_cents, invoice.currency)}
          </p>
        </div>
      </div>

      {(invoice.line_items || []).length > 0 && (
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-[0.12em] text-neutral-500">
              <th className="pb-1">Category</th>
              <th className="pb-1">Description</th>
              <th className="pb-1 text-right">Qty</th>
              <th className="pb-1 text-right">Unit</th>
              <th className="pb-1 text-right">Line total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.line_items.map((li) => (
              <tr key={li.id} className="border-t border-neutral-100 text-sm">
                <td className="py-1.5 text-neutral-500">
                  {LINE_CATEGORY_LABELS[li.category] || li.category}
                </td>
                <td className="py-1.5 text-neutral-800">{li.description}</td>
                <td className="py-1.5 text-right">{li.quantity}</td>
                <td className="py-1.5 text-right font-mono">
                  {(li.unit_amount_cents / 100).toFixed(2)}
                </td>
                <td className="py-1.5 text-right font-mono">
                  {((li.quantity * li.unit_amount_cents) / 100).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button
          type="button"
          data-testid={`admin-invoice-pdf-${invoice.id}`}
          onClick={onDownloadPdf}
          disabled={pdfBusy}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
        >
          <Receipt size={12} weight="duotone" />
          {pdfBusy ? "Preparing…" : "PDF"}
        </button>
        {invoice.status !== "draft" && (
          <button
            type="button"
            data-testid={`admin-invoice-email-${invoice.id}`}
            onClick={onEmailInvoice}
            disabled={emailBusy}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60"
          >
            <Envelope size={12} weight="duotone" />
            {emailBusy
              ? "Sending…"
              : invoice.invoice_emailed_at
                ? "Email again"
                : "Email PDF"}
          </button>
        )}
        {invoice.status === "draft" && (
          <button
            type="button"
            data-testid={`admin-invoice-issue-${invoice.id}`}
            onClick={onIssue}
            disabled={invoice.amount_total_cents <= 0}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            Issue to family
          </button>
        )}
        {(invoice.status === "issued" || invoice.status === "failed") && (
          <button
            type="button"
            data-testid={`admin-invoice-mark-paid-${invoice.id}`}
            onClick={onMarkPaid}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5"
          >
            Record manual payment
          </button>
        )}
        {invoice.status !== "void" && invoice.status !== "paid" && (
          <button
            type="button"
            data-testid={`admin-invoice-void-${invoice.id}`}
            onClick={onVoid}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#C6851E]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#C6851E] hover:bg-[#C6851E]/10"
          >
            <TrashSimple size={12} weight="bold" />
            Void
          </button>
        )}
      </div>
    </div>
  );
}

function StatusTag({ status }) {
  const map = {
    draft:  "bg-neutral-100 text-neutral-600 border-neutral-200",
    issued: "bg-secondary/30 text-neutral-900 border-secondary/50",
    paid:   "bg-primary/10 text-primary-hover border-primary/30",
    failed: "bg-[#C6851E]/10 text-[#C6851E] border-[#C6851E]/40",
    void:   "bg-neutral-200 text-neutral-500 border-neutral-300",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${map[status] || map.draft}`}
    >
      {invoiceStatusLabel(status)}
    </span>
  );
}

function NewInvoiceForm({ onCancel, onSubmit }) {
  const [title, setTitle] = useState("Monthly tuition");
  const [dueDate, setDueDate] = useState("");
  const [lines, setLines] = useState([
    { category: "tuition", description: "Tuition — current month", quantity: 1, unitAmountCents: 120000 },
  ]);

  const total = useMemo(
    () => lines.reduce((a, l) => a + l.quantity * l.unitAmountCents, 0),
    [lines],
  );

  const update = (i, patch) =>
    setLines((prev) => prev.map((l, idx) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () =>
    setLines((prev) => [
      ...prev,
      { category: "other", description: "", quantity: 1, unitAmountCents: 0 },
    ]);
  const removeLine = (i) =>
    setLines((prev) => prev.filter((_, idx) => idx !== i));

  const canSubmit =
    title.trim().length > 1 &&
    lines.every((l) => l.description.trim() && l.unitAmountCents >= 0) &&
    total > 0;

  return (
    <form
      data-testid="billing-new-invoice-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ title: title.trim(), dueDate, lines });
      }}
      className="space-y-4 rounded-2xl border border-primary/30 bg-primary/5 p-5"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Title
          </span>
          <input
            data-testid="billing-new-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Due date (optional)
          </span>
          <input
            data-testid="billing-new-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className={inputClass}
          />
        </label>
      </div>

      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Line items
        </p>
        <ul className="space-y-2">
          {lines.map((l, i) => (
            <li
              key={i}
              data-testid={`billing-new-line-${i}`}
              className="grid grid-cols-1 items-end gap-2 rounded-2xl border border-neutral-200 bg-white p-3 sm:grid-cols-[auto_1fr_auto_auto_auto]"
            >
              <select
                data-testid={`billing-new-line-${i}-category`}
                value={l.category}
                onChange={(e) => update(i, { category: e.target.value })}
                className={inputClass}
              >
                {LINE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{LINE_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
              <input
                data-testid={`billing-new-line-${i}-desc`}
                value={l.description}
                onChange={(e) => update(i, { description: e.target.value })}
                placeholder="Description (e.g. Tuition — April)"
                className={inputClass}
              />
              <input
                data-testid={`billing-new-line-${i}-qty`}
                type="number"
                min="1"
                value={l.quantity}
                onChange={(e) => update(i, { quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                className={`${inputClass} w-20`}
              />
              <input
                data-testid={`billing-new-line-${i}-amt`}
                type="number"
                step="0.01"
                min="0"
                value={(l.unitAmountCents / 100).toFixed(2)}
                onChange={(e) => {
                  const dollars = parseFloat(e.target.value || "0");
                  update(i, { unitAmountCents: Math.round(dollars * 100) });
                }}
                className={`${inputClass} w-28 font-mono`}
              />
              <button
                type="button"
                data-testid={`billing-new-line-${i}-remove`}
                onClick={() => removeLine(i)}
                disabled={lines.length === 1}
                aria-label="Remove line item"
                className="rounded-full p-2 text-neutral-400 hover:bg-[#C6851E]/10 hover:text-[#C6851E] disabled:opacity-40"
              >
                <TrashSimple size={14} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          data-testid="billing-new-add-line"
          onClick={addLine}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5"
        >
          <Plus size={12} weight="bold" />
          Add line
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3">
        <div className="text-sm text-neutral-700">
          <span className="font-semibold">Total</span>{" "}
          <span className="font-mono">{centsToUsd(total)}</span>
          {total <= 0 && (
            <span
              data-testid="billing-new-zero-hint"
              className="ml-3 text-xs text-[#C6851E]"
            >
              Total must be greater than $0 to save as a draft.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="billing-new-cancel"
            onClick={onCancel}
            className="rounded-full px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="billing-new-submit"
            disabled={!canSubmit}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            Save draft
          </button>
        </div>
      </div>
    </form>
  );
}

const inputClass =
  "rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";
